import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import api from '../services/api';
import {
  MAX_DELIVERY_PHOTOS,
  buildAddDeliveryPhotosFormData,
  getCurrentLocation,
} from '../utils/deliveryPhotos';

/**
 * Form to add more delivery photos to an already-completed job.
 */
export default function AddDeliveryPhotos({ jobId, existingPhotoCount = 0, onUploaded }) {
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [gps, setGps] = useState({ latitude: null, longitude: null });

  const totalCount = existingPhotoCount + pendingPhotos.length;
  const atLimit = totalCount >= MAX_DELIVERY_PHOTOS;
  const remainingSlots = Math.max(0, MAX_DELIVERY_PHOTOS - existingPhotoCount - pendingPhotos.length);

  const handlePhotoInput = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    e.target.value = '';
    setError('');

    let plat = gps.latitude;
    let plng = gps.longitude;
    if (plat == null || plng == null) {
      try {
        const loc = await getCurrentLocation();
        plat = loc.latitude;
        plng = loc.longitude;
        setGps({ latitude: plat, longitude: plng });
      } catch (err) {
        console.warn('Could not get GPS for delivery photos:', err);
      }
    }

    setPendingPhotos((prev) => {
      const remaining = MAX_DELIVERY_PHOTOS - existingPhotoCount - prev.length;
      if (remaining <= 0) {
        setError(`Maximum of ${MAX_DELIVERY_PHOTOS} delivery photos per job.`);
        return prev;
      }
      const slice = selectedFiles.slice(0, remaining);
      if (selectedFiles.length > remaining) {
        setError(`Only ${remaining} more photo(s) allowed (${MAX_DELIVERY_PHOTOS} maximum).`);
      }
      const next = [...prev];
      slice.forEach((file) => {
        next.push({
          id: `${Date.now()}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
          latitude: plat ?? null,
          longitude: plng ?? null,
        });
      });
      return next;
    });
  };

  const removePendingPhoto = (id) => {
    setPendingPhotos((prev) => {
      const row = prev.find((p) => p.id === id);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleUpload = async () => {
    if (!pendingPhotos.length) {
      setError('Select at least one photo to upload.');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = buildAddDeliveryPhotosFormData({
        deliveryPhotos: pendingPhotos,
        latitude: gps.latitude,
        longitude: gps.longitude,
      });
      const response = await api.post(`/jobs/${jobId}/delivery-photos/upload/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      pendingPhotos.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
      setPendingPhotos([]);
      if (onUploaded) {
        onUploaded(response.data?.job || response.data);
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to upload photos. Please try again.';
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  if (existingPhotoCount >= MAX_DELIVERY_PHOTOS) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        This job already has the maximum of {MAX_DELIVERY_PHOTOS} delivery photos.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Add more photos
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Up to {MAX_DELIVERY_PHOTOS} photos per job ({existingPhotoCount} uploaded, {remainingSlots}{' '}
        slot{remainingSlots === 1 ? '' : 's'} remaining). JPEG, PNG, or WebP. GPS is taken from photo
        EXIF when available; otherwise your current device location is used.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Button
          type="button"
          variant="outlined"
          size="small"
          startIcon={<AddPhotoAlternateIcon />}
          tabIndex={-1}
          aria-hidden
          sx={{ pointerEvents: 'none' }}
          disabled={atLimit || uploading}
        >
          Choose photos ({totalCount}/{MAX_DELIVERY_PHOTOS})
        </Button>
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/*"
          capture="environment"
          multiple
          disabled={atLimit || uploading}
          aria-label="Add delivery photos"
          onChange={handlePhotoInput}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: atLimit || uploading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            zIndex: 2,
          }}
        />
      </Box>

      {pendingPhotos.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {pendingPhotos.map((p) => (
            <Box key={p.id} sx={{ position: 'relative', width: 88, height: 88 }}>
              <Box
                component="img"
                src={p.previewUrl}
                alt=""
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
              <IconButton
                size="small"
                color="error"
                disabled={uploading}
                onClick={() => removePendingPhoto(p.id)}
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  p: 0.25,
                }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {pendingPhotos.length > 0 && (
        <Button
          variant="contained"
          size="small"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          onClick={handleUpload}
          disabled={uploading}
          sx={{ mt: 1.5 }}
        >
          {uploading ? 'Uploading…' : `Upload ${pendingPhotos.length} photo${pendingPhotos.length === 1 ? '' : 's'}`}
        </Button>
      )}
    </Box>
  );
}
